'use client';

import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { CheckCircle2, Loader2, School2, X } from 'lucide-react';
import { apiClient } from '@/lib/api-client';
import useAuthStore from '@/store/useAuthStore';
import {
  mergeClassJoinPromptIntoOnboardData,
  type ClassJoinPromptSource,
} from '@/lib/class-join-prompt';

interface JoinClassCodeModalProps {
  visible: boolean;
  onClose: () => void;
  source?: ClassJoinPromptSource;
  persistDismissal?: boolean;
  secondaryLabel?: string;
  replaceExisting?: boolean;
  onJoined?: () => void;
}

type JoinClassResponse = {
  success?: boolean;
  already_joined?: boolean;
  replaced_existing?: boolean;
  teacher?: {
    id: string;
    name: string | null;
  } | null;
  error?: string;
};

export default function JoinClassCodeModal({
  visible,
  onClose,
  source = 'manual',
  persistDismissal = false,
  secondaryLabel = 'Skip for now',
  replaceExisting = false,
  onJoined,
}: JoinClassCodeModalProps) {
  const profile = useAuthStore((s) => s.profile);
  const updateProfile = useAuthStore((s) => s.updateProfile);
  const [mounted, setMounted] = useState(false);
  const [classCode, setClassCode] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [joinedTeacherName, setJoinedTeacherName] = useState<string | null>(null);
  const [resultState, setResultState] = useState<'idle' | 'joined' | 'already_joined'>('idle');

  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  useEffect(() => {
    if (!visible) {
      setClassCode('');
      setIsSubmitting(false);
      setError(null);
      setJoinedTeacherName(null);
      setResultState('idle');
    }
  }, [visible]);

  const normalizedCode = useMemo(() => classCode.trim().toUpperCase(), [classCode]);

  const persistPromptState = async (status: 'skipped' | 'joined') => {
    const timestamp = new Date().toISOString();
    await updateProfile({
      onboard_data: mergeClassJoinPromptIntoOnboardData(profile, {
        status,
        completed_at: timestamp,
        joined_via: status === 'joined' ? source : undefined,
      }),
    });
  };

  const handleDismiss = async () => {
    if (persistDismissal) {
      try {
        await persistPromptState('skipped');
      } catch (persistError) {
        console.warn('Failed to persist skipped class join prompt:', persistError);
      }
    }
    onClose();
  };

  const handleJoinClass = async () => {
    if (!normalizedCode || isSubmitting) {
      if (!normalizedCode) {
        setError('Please enter your class code.');
      }
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const response = await apiClient.post('/api/teacher-tools/join-class', {
        teacher_code: normalizedCode,
        replace_existing: replaceExisting,
      });
      const data = await response.json().catch(() => ({} as JoinClassResponse));

      if (!response.ok || !data.success) {
        if (response.status === 404) {
          setError("That class code doesn't look right.");
          return;
        }
        if (response.status === 409) {
          setError(data.error || 'You can only join one class right now.');
          return;
        }
        setError(data.error || "We couldn't join the class right now. Please try again.");
        return;
      }

      setJoinedTeacherName(data.teacher?.name || null);
      setResultState(data.already_joined ? 'already_joined' : 'joined');

      try {
        await persistPromptState('joined');
      } catch (persistError) {
        console.warn('Failed to persist joined class prompt:', persistError);
      }

      onJoined?.();
    } catch (joinError) {
      console.error('Failed to join class:', joinError);
      setError("We couldn't join the class right now. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!visible || !mounted) return null;

  const isResolved = resultState !== 'idle';
  const teacherLine = joinedTeacherName ? `Connected to ${joinedTeacherName}.` : 'You can keep learning with your class on Pengi.';

  return createPortal(
    <div
      className="fixed inset-0 z-[1200] flex items-center justify-center bg-[rgba(15,23,42,0.55)] px-4"
      onClick={() => {
        void handleDismiss();
      }}
    >
      <div
        className="relative w-full max-w-[520px] rounded-[28px] border border-[#E8DDCF] bg-[#F8F5EE] px-6 pb-6 pt-7 shadow-[0_26px_80px_rgba(15,23,42,0.22)]"
        onClick={(event) => event.stopPropagation()}
      >
        <button
          type="button"
          onClick={() => {
            void handleDismiss();
          }}
          className="absolute right-4 top-4 flex h-10 w-10 items-center justify-center rounded-full text-[#4B5563] transition-colors hover:bg-[#EEE9DF] hover:text-[#111827]"
          aria-label="Close join class dialog"
        >
          <X className="h-4.5 w-4.5" />
        </button>

        <div className="flex flex-col items-center text-center">
          <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-[#FFF4DE]">
            {isResolved ? (
              <CheckCircle2 className="h-8 w-8 text-[#42C62C]" />
            ) : (
              <School2 className="h-8 w-8 text-[#FBAF2C]" />
            )}
          </div>

          <h3 className="display-text-bold text-center text-2xl leading-tight text-[#1E1E20] sm:text-[1.85rem]">
            {isResolved
              ? resultState === 'already_joined'
                ? "You're already in this class"
                : replaceExisting
                  ? 'Your class has been updated'
                  : "You've joined your class"
              : replaceExisting
                ? 'Change your class'
                : "Join your teacher's class"}
          </h3>

          <p className="display-text mt-3 max-w-[360px] text-sm leading-6 text-[#374151]">
            {isResolved
              ? teacherLine
              : replaceExisting
                ? 'Enter a new class code to switch to a different teacher on Pengi.'
                : 'Enter your class code to join your teacher on Pengi.'}
          </p>

          {!isResolved && (
            <div className="mt-5 w-full text-left">
              <label htmlFor="class-code" className="display-text-semi mb-2 block text-sm text-[#374151]">
                Class code
              </label>
              <input
                id="class-code"
                type="text"
                value={classCode}
                onChange={(event) => setClassCode(event.target.value.toUpperCase())}
                placeholder="Enter class code"
                maxLength={20}
                className="display-text w-full rounded-[14px] border border-[#D7CCBC] bg-white px-4 py-3 text-sm uppercase text-[#111827] placeholder:normal-case placeholder:text-[#9CA3AF] focus:border-[#FBAF2C] focus:outline-none focus:ring-1 focus:ring-[#FBAF2C]"
              />
              {error && (
                <p className="display-text mt-2 text-sm text-[#DC2626]">{error}</p>
              )}
            </div>
          )}

          <div className="mt-6 flex w-full flex-col gap-2">
            {isResolved ? (
              <button
                type="button"
                onClick={onClose}
                className="display-text-semi w-full cursor-pointer rounded-xl bg-[#42C62C] py-3 text-sm text-white transition-colors hover:bg-[#38A824]"
              >
                Continue
              </button>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => {
                    void handleJoinClass();
                  }}
                  disabled={isSubmitting}
                  className="display-text-semi flex w-full cursor-pointer items-center justify-center gap-2 rounded-xl bg-[#42C62C] py-3 text-sm text-white transition-colors hover:bg-[#38A824] disabled:cursor-not-allowed disabled:opacity-70"
                >
                  {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  {replaceExisting ? 'Change class' : 'Join class'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    void handleDismiss();
                  }}
                  className="display-text-semi w-full cursor-pointer rounded-xl bg-[#ECEEF2] py-3 text-sm text-[#2F3A4A] transition-colors hover:bg-[#E2E6EC]"
                >
                  {secondaryLabel}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
